import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Moon, Sun, Fingerprint, Check, ShieldCheck } from 'lucide-react'
import { startAuthentication } from '@simplewebauthn/browser'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { applyThemeBuilderColors } from '../lib/themeBuilder'

/* ── Platform detection ──────────────────────────────────────────────── */

function getBiometricInfo() {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return { label: 'Face ID', icon: 'face' }
  if (/iPad/.test(ua)) return { label: 'Touch ID', icon: 'fingerprint' }
  if (/Mac/.test(ua)) return { label: 'Touch ID', icon: 'fingerprint' }
  if (/Windows/.test(ua)) return { label: 'Windows Hello', icon: 'fingerprint' }
  return { label: 'Biometrics', icon: 'fingerprint' }
}

/* ── Shared styles ───────────────────────────────────────────────────── */

const labelStyle = {
  fontFamily: '"DM Sans", sans-serif',
  fontSize: 9,
  letterSpacing: '0.18em',
  color: '#444',
  marginBottom: 8,
}

const inputStyle = {
  width: '100%',
  background: 'transparent',
  border: 'none',
  borderBottom: '0.5px solid #333',
  borderRadius: 0,
  color: '#e8e8e8',
  fontFamily: '"DM Sans", sans-serif',
  fontSize: 14,
  paddingBottom: 10,
  outline: 'none',
}

const btnStyle = (opts = {}) => ({
  width: '100%',
  height: 40,
  background: 'transparent',
  color: opts.color || '#e8e8e8',
  border: `0.5px solid ${opts.borderColor || '#444'}`,
  borderRadius: 5,
  fontFamily: '"DM Sans", sans-serif',
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: '0.14em',
  cursor: opts.disabled ? 'default' : 'pointer',
  opacity: opts.disabled ? 0.5 : 1,
  transition: 'border-color 0.2s, color 0.2s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  ...opts.extra,
})

/* ── Wordmark (shared across all phases) ─────────────────────────────── */

function Wordmark() {
  return (
    <div className="mb-12">
      <div
        className="uppercase mb-2"
        style={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: 9,
          fontWeight: 400,
          letterSpacing: '0.20em',
          lineHeight: 1.2,
          color: '#555',
        }}
      >
        Big World Capital
      </div>
      <h1
        className="uppercase"
        style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: 36,
          fontWeight: 400,
          letterSpacing: '0.22em',
          lineHeight: 1.0,
          color: '#e8e8e8',
        }}
      >
        Erevis
      </h1>
    </div>
  )
}

/* ── Main Component ──────────────────────────────────────────────────── */

export default function AuthPage() {
  const {
    user, signIn, signInWithPasskey, signInWithDiscoverablePasskey,
    signInWithPasswordAndBiometric,
    completeBiometric, cancelBiometric, clearBiometricPending, biometricPending,
  } = useAuth()

  const [kickedMessage, setKickedMessage] = useState(() => {
    // FA-01-session-reliability (FA-01-11): banner key moved to
    // localStorage so it stays paired with the session_token / registered_at
    // keys after their migration off sessionStorage. Same-browser tabs
    // need the same surface either way (this key is read-on-mount and
    // cleared immediately).
    const kicked = localStorage.getItem('erevis_kicked')
    if (kicked) {
      localStorage.removeItem('erevis_kicked')
      return kicked
    }
    return null
  })

  // FA-01-passkey-hardening (FA-01-09b): companion banner to the
  // restore-path signOut-on-unknown behavior in useAuth.jsx. Without
  // this, the user is just dropped on the login screen with no
  // explanation of why they got signed out.
  const [statusUnknownMessage] = useState(() => {
    const flag = sessionStorage.getItem('erevis_passkey_status_unknown')
    if (flag) {
      sessionStorage.removeItem('erevis_passkey_status_unknown')
      return 'Passkey status unavailable. Please sign in again.'
    }
    return null
  })

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [showPasswordFallback, setShowPasswordFallback] = useState(false)

  // Phase: 'login' | 'verifying' | 'verified' | 'error'
  const [phase, setPhase] = useState('login')
  const [bioError, setBioError] = useState('')
  // Stash authOptions from signInWithPasswordAndBiometric for the biometric screen
  const [pendingAuthOptions, setPendingAuthOptions] = useState(null)

  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('bwc-dark-mode')
    return saved !== null ? saved === 'true' : true
  })

  const isDesktop = useMemo(() => window.matchMedia('(pointer: fine)').matches, [])
  const isMobile = !isDesktop
  const bioInfo = useMemo(() => getBiometricInfo(), [])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark)
    applyThemeBuilderColors(dark ? 'dark' : 'light')
  }, [dark])

  const toggleDark = () => {
    setDark(d => {
      const next = !d
      localStorage.setItem('bwc-dark-mode', String(next))
      window.dispatchEvent(new Event('bwc-theme-change'))
      return next
    })
  }

  /* ── Biometric verification (Phase 2 → 3 or error) ─────────────── */

  // FA-01-hotfix-dual-ceremony (2026-05-23): no longer called by the
  // password handlers (Option F). This function and its supporting state
  // (pendingAuthOptions, phase='verifying'/'verified'/'error') may now be
  // dormant — kept in place to keep this hotfix narrowly scoped. If a
  // future slice re-introduces a call path that fires alongside the
  // restore-path autoVerifyTriggered effect, the dual-ceremony race that
  // broke prod 2026-05-23 will reappear.
  const triggerBiometric = useCallback(async (authOptions) => {
    try {
      // Trigger native Touch ID / Face ID
      const assertion = await startAuthentication({ optionsJSON: authOptions.options })

      // Verify with backend
      const verifyRes = await fetch('/api/auth/passkey-auth-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, response: assertion }),
      })
      const verifyData = await verifyRes.json()

      if (!verifyRes.ok) {
        setBioError('Verification failed')
        setPhase('error')
        return
      }

      // Success — show verified screen
      setPhase('verified')
      // FA-01-10: completeBiometric now returns { success, error? } so
      // we can surface session-register failures to the user instead of
      // silently landing them on the workspace.
      setTimeout(async () => {
        const r = await completeBiometric()
        if (r && r.error) {
          setBioError(r.error)
          setPhase('error')
        }
      }, 800)
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setBioError('Verification cancelled')
        setPhase('error')
        // Auto-transition back to login after 1 second
        setTimeout(async () => {
          await cancelBiometric()
          setPhase('login')
          setBioError('')
        }, 1000)
      } else {
        setBioError(err.message || 'Verification failed')
        setPhase('error')
      }
    }
  }, [email, completeBiometric, cancelBiometric])

  /* ── Desktop sign-in handler ────────────────────────────────────── */

  const handleDesktopSignIn = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const result = await signInWithPasswordAndBiometric(email, password)

    if (result.error) {
      setError(result.error.message || result.error)
      setLoading(false)
      return
    }

    if (result.biometricRequired) {
      // FA-01-hotfix-dual-ceremony (2026-05-23): password sign-in defers
      // entirely to the restore-path UI for the WebAuthn ceremony. Calling
      // triggerBiometric() here would race with the autoVerifyTriggered
      // useEffect, causing one ceremony to abort the other. The restore-path
      // UI + handleReVerify own the single ceremony.
      setLoading(false)
      return
    }

    // No passkeys — login complete, gate released by useAuth
    setLoading(false)
  }

  /* ── Mobile sign-in handlers ────────────────────────────────────── */

  const handleBiometricSignIn = async () => {
    setError('')
    setPasskeyLoading(true)
    try {
      const result = await signInWithDiscoverablePasskey()
      if (result.error) setError(result.error)
    } catch (err) {
      setError(err.message || 'Biometric sign-in failed')
    }
    setPasskeyLoading(false)
  }

  const handleMobilePasswordSignIn = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    // FA-01-passkey-hardening: mobile password fallback must route
    // through the same fail-closed pipeline as desktop. Plain signIn()
    // would bypass the biometric gate entirely for mobile users with
    // registered passkeys. Closes the mobile half of FA-01-09.
    //
    // FA-01-hotfix-dual-ceremony (2026-05-23): when biometricRequired,
    // defer entirely to the restore-path UI for the WebAuthn ceremony.
    // See handleDesktopSignIn for the dual-ceremony race notes.
    const result = await signInWithPasswordAndBiometric(email, password)
    if (result?.error) {
      setError(typeof result.error === 'string' ? result.error : (result.error.message || 'Sign-in failed'))
      setLoading(false)
      return
    }
    setLoading(false)
  }

  /* ── Error phase: retry handler ─────────────────────────────────── */

  const handleRetry = () => {
    setBioError('')
    setPhase('verifying')
    setTimeout(() => triggerBiometric(pendingAuthOptions), 100)
  }

  const handleCancelBiometric = async () => {
    await cancelBiometric()
    setPhase('login')
    setBioError('')
    setPendingAuthOptions(null)
  }

  /* ── Session restore re-verification ──────────────────────────────── */

  const handleReVerify = async () => {
    setPasskeyLoading(true)
    setError('')
    try {
      const challengeRes = await fetch('/api/auth/biometric-challenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      })
      const challengeData = await challengeRes.json()
      if (!challengeRes.ok) throw new Error(challengeData.error || 'Challenge failed')

      // FA-01-passkey-hardening: do NOT clear the gate here. The
      // session-restore decision was made by /api/auth/passkey-status
      // (authoritative). Once biometricPending is true, the ONLY release
      // path is a successful biometric-verify round-trip. If biometric-
      // challenge disagrees, treat it as the authoritative endpoint
      // contradicting itself — surface an error, do not release.
      if (!challengeData.hasPasskeys) {
        console.error('[AuthPage] biometric-challenge reported hasPasskeys=false during session restore; passkey-status was authoritative')
        setError('Passkey status mismatch. Please sign out and back in.')
        setPasskeyLoading(false)
        return
      }

      const { challengeId, hasPasskeys, ...authOptions } = challengeData

      const { startAuthentication } = await import('@simplewebauthn/browser')
      const assertion = await startAuthentication({ optionsJSON: authOptions })

      const verifyRes = await fetch('/api/auth/biometric-verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({ response: assertion, challengeId }),
      })
      const verifyData = await verifyRes.json()
      if (!verifyRes.ok || !verifyData.verified) {
        setError('Verification failed')
        setPasskeyLoading(false)
        return
      }

      // FA-01-10: clearBiometricPending now returns { success, error? }.
      // Surface a session-register failure instead of silently dropping
      // the user onto the workspace.
      const r = await clearBiometricPending()
      if (r && r.error) {
        setError(r.error)
        setPasskeyLoading(false)
        return
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Verification cancelled')
      } else {
        setError(err.message || 'Verification failed')
      }
    }
    setPasskeyLoading(false)
  }

  // Auto-trigger biometric prompt on session restore
  const autoVerifyTriggered = useRef(false)
  useEffect(() => {
    if (user && biometricPending && !autoVerifyTriggered.current) {
      autoVerifyTriggered.current = true
      const timer = setTimeout(() => handleReVerify(), 300)
      return () => clearTimeout(timer)
    }
  }, [user, biometricPending]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleReVerifySignOut = async () => {
    await cancelBiometric()
  }

  /* ── Render ─────────────────────────────────────────────────────── */

  // Session restore: user exists but biometric re-verification required
  if (user && biometricPending) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 relative"
        style={{ backgroundColor: '#0d0d0d' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0.08) 0%, transparent 65%)' }} />
        <div className="w-full relative z-10" style={{ maxWidth: 360 }}>
          <Wordmark />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 24 }}>
            <div className="bio-pulse" style={{
              width: 96, height: 96, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 32,
            }}>
              <Fingerprint size={48} style={{ color: 'rgba(220, 220, 220, 0.82)' }} strokeWidth={1.5} />
            </div>

            {!error && passkeyLoading && (
              <div style={{
                fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#555',
                textAlign: 'center', lineHeight: 1.6, maxWidth: 240, marginBottom: 24,
              }}>
                Verifying...
              </div>
            )}

            {!error && !passkeyLoading && (
              <div style={{
                fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#555',
                textAlign: 'center', lineHeight: 1.6, maxWidth: 240, marginBottom: 24,
              }}>
                Verify your identity to continue
              </div>
            )}

            {error && (
              <>
                <p style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#e05a4a', marginBottom: 16 }}>
                  {error}
                </p>
                <button
                  type="button"
                  onClick={() => { autoVerifyTriggered.current = false; handleReVerify() }}
                  disabled={passkeyLoading}
                  style={btnStyle({ disabled: passkeyLoading, extra: { width: 240, marginBottom: 16 } })}
                >
                  <Fingerprint size={15} />
                  {passkeyLoading ? 'Verifying...' : `Retry ${bioInfo.label}`}
                </button>
              </>
            )}

            <button
              type="button"
              onClick={handleReVerifySignOut}
              style={{
                background: 'none', border: 'none', color: '#555',
                fontFamily: '"DM Sans", sans-serif', fontSize: 11,
                cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3,
              }}
            >
              Sign out
            </button>
          </div>
        </div>

        <style>{`
          @keyframes bioPulse {
            0%, 100% { box-shadow: 0 0 20px rgba(255, 255, 255, 0.10); }
            50% { box-shadow: 0 0 40px rgba(255, 255, 255, 0.18); }
          }
          .bio-pulse { animation: bioPulse 2s ease-in-out infinite; }
        `}</style>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{ backgroundColor: '#0d0d0d' }}
    >
      {/* Subtle radial vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0.08) 0%, transparent 65%)' }}
      />

      <div className="w-full relative z-10" style={{ maxWidth: 360, minHeight: 320 }}>
        {/* Wordmark — always visible */}
        <Wordmark />

        {/* Kicked notification */}
        {kickedMessage && (
          <div className="mb-4 px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10">
            <p className="text-[11px] text-amber-400 font-mono text-center">
              Session ended {'\u2014'} signed in on {kickedMessage}
            </p>
          </div>
        )}

        {/* FA-01-passkey-hardening: passkey-status-unknown banner. The
            user was forcibly signed out by useAuth's restore path because
            /api/auth/passkey-status didn't return a definitive answer. */}
        {statusUnknownMessage && (
          <div className="mb-4 px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10">
            <p className="text-[11px] text-amber-400 font-mono text-center">
              {statusUnknownMessage}
            </p>
          </div>
        )}

        {/* Phase container — relative so phases can crossfade */}
        <div className="relative">

          {/* ── PHASE 1: Login Form ────────────────────────────────── */}
          <div
            style={{
              opacity: phase === 'login' ? 1 : 0,
              transform: phase === 'login' ? 'translateX(0)' : 'translateX(-30px)',
              transition: 'opacity 400ms ease, transform 400ms ease',
              pointerEvents: phase === 'login' ? 'auto' : 'none',
              position: phase === 'login' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
            }}
          >
            {/* Desktop */}
            {isDesktop && (
              <form onSubmit={handleDesktopSignIn} className="space-y-6">
                <div>
                  <label className="block uppercase" style={labelStyle}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="block uppercase" style={labelStyle}>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={inputStyle}
                  />
                </div>

                {error && (
                  <p style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#e05a4a' }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={btnStyle({ disabled: loading, extra: { marginTop: 24 } })}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.borderColor = '#666' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#444' }}
                >
                  {loading ? 'Verifying...' : 'Sign In'}
                </button>
              </form>
            )}

            {/* Mobile */}
            {isMobile && (
              <div className="space-y-6">
                <button
                  type="button"
                  onClick={handleBiometricSignIn}
                  disabled={passkeyLoading}
                  style={btnStyle({ disabled: passkeyLoading })}
                  onMouseEnter={e => { if (!passkeyLoading) e.currentTarget.style.borderColor = '#666' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#444' }}
                >
                  <Fingerprint size={15} />
                  {passkeyLoading ? 'Verifying...' : `Sign in with ${bioInfo.label}`}
                </button>

                {!showPasswordFallback ? (
                  <div style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => setShowPasswordFallback(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#555',
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: 11,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textUnderlineOffset: 3,
                      }}
                    >
                      Use password instead
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleMobilePasswordSignIn} className="space-y-6">
                    <div>
                      <label className="block uppercase" style={labelStyle}>Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className="block uppercase" style={labelStyle}>Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="••••••••"
                        autoFocus
                        style={inputStyle}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      style={btnStyle({ disabled: loading, color: '#aaa', borderColor: '#333' })}
                      onMouseEnter={e => { if (!loading) e.currentTarget.style.borderColor = '#555' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#333' }}
                    >
                      {loading ? 'Loading...' : 'Sign In with Password'}
                    </button>
                  </form>
                )}

                {error && (
                  <p style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#e05a4a' }}>
                    {error}
                  </p>
                )}
              </div>
            )}

            {/* Hairline rule */}
            <div style={{ borderTop: '0.5px solid #1a1a1a', marginTop: 24 }} />
          </div>

          {/* ── PHASE 2: Biometric Verification Screen ─────────────── */}
          <div
            style={{
              opacity: phase === 'verifying' ? 1 : 0,
              transform: phase === 'verifying' ? 'translateX(0)' : 'translateX(30px)',
              transition: 'opacity 400ms ease, transform 400ms ease',
              pointerEvents: phase === 'verifying' ? 'auto' : 'none',
              position: phase === 'verifying' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 24 }}>
              {/* Pulsing fingerprint icon */}
              <div className="bio-pulse" style={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 32,
              }}>
                <Fingerprint size={48} style={{ color: 'rgba(220, 220, 220, 0.82)' }} strokeWidth={1.5} />
              </div>

              <div
                className="uppercase"
                style={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: '0.22em',
                  color: '#e8e8e8',
                  marginBottom: 10,
                }}
              >
                {bioInfo.label} Required
              </div>

              <div
                style={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 11,
                  color: '#555',
                  textAlign: 'center',
                  lineHeight: 1.6,
                  maxWidth: 240,
                  marginBottom: 32,
                }}
              >
                {bioInfo.icon === 'face'
                  ? 'Look at your device to verify your identity'
                  : 'Place your finger on the sensor to verify your identity'}
              </div>

              <button
                type="button"
                onClick={handleCancelBiometric}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#555',
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 11,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                Cancel
              </button>
            </div>
          </div>

          {/* ── PHASE 3: Identity Verified ──────────────────────────── */}
          <div
            style={{
              opacity: phase === 'verified' ? 1 : 0,
              transform: phase === 'verified' ? 'scale(1)' : 'scale(0.9)',
              transition: 'opacity 400ms ease, transform 400ms ease',
              pointerEvents: phase === 'verified' ? 'auto' : 'none',
              position: phase === 'verified' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 24 }}>
              <div className="verified-glow" style={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 32,
              }}>
                <ShieldCheck size={48} strokeWidth={1.5} />
              </div>

              <div
                className="uppercase"
                style={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: '0.22em',
                  color: 'rgba(80, 200, 120, 0.9)',
                }}
              >
                Identity Verified
              </div>
            </div>
          </div>

          {/* ── ERROR Phase ─────────────────────────────────────────── */}
          <div
            style={{
              opacity: phase === 'error' ? 1 : 0,
              transform: phase === 'error' ? 'scale(1)' : 'scale(0.95)',
              transition: 'opacity 400ms ease, transform 400ms ease',
              pointerEvents: phase === 'error' ? 'auto' : 'none',
              position: phase === 'error' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 24 }}>
              <div style={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 30px rgba(224, 90, 74, 0.2)',
                marginBottom: 32,
              }}>
                <Fingerprint size={48} style={{ color: 'rgba(224, 90, 74, 0.8)' }} strokeWidth={1.5} />
              </div>

              <div
                className="uppercase"
                style={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: '0.22em',
                  color: '#e05a4a',
                  marginBottom: 10,
                }}
              >
                {bioError === 'Verification cancelled' ? 'Verification Cancelled' : 'Verification Failed'}
              </div>

              {bioError !== 'Verification cancelled' && (
                <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                  <button
                    type="button"
                    onClick={handleRetry}
                    style={btnStyle({ extra: { width: 'auto', paddingLeft: 24, paddingRight: 24 } })}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#666' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#444' }}
                  >
                    Try Again
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelBiometric}
                    style={btnStyle({ color: '#666', borderColor: '#333', extra: { width: 'auto', paddingLeft: 24, paddingRight: 24 } })}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#555' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#333' }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Floating dark mode toggle */}
      <div className="fixed bottom-5 right-5 z-40">
        <button
          onClick={toggleDark}
          title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-canvas2 border border-border text-inkMuted hover:text-ink hover:border-ink/30 shadow-md transition-all"
        >
          {dark ? <Sun size={15} /> : <Moon size={15} />}
        </button>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes bioPulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(255, 255, 255, 0.10);
          }
          50% {
            box-shadow: 0 0 40px rgba(255, 255, 255, 0.18);
          }
        }
        .bio-pulse {
          animation: bioPulse 2s ease-in-out infinite;
        }

        @keyframes verifiedGlow {
          0% {
            color: rgba(220, 220, 220, 0.82);
            box-shadow: 0 0 20px rgba(255, 255, 255, 0.12);
            transform: scale(0.85);
          }
          40% {
            color: rgba(80, 200, 120, 0.9);
            box-shadow: 0 0 35px rgba(80, 200, 120, 0.25);
            transform: scale(1.05);
          }
          100% {
            color: rgba(80, 200, 120, 0.8);
            box-shadow: 0 0 25px rgba(80, 200, 120, 0.15);
            transform: scale(1);
          }
        }
        .verified-glow {
          animation: verifiedGlow 600ms ease-out forwards;
        }
      `}</style>
    </div>
  )
}
